import { DataGrid, type GridColumn } from "@/components/data-grid";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { ColumnSemantic, Presentation } from "@lala/harness/protocol";
import { useMemo } from "react";

import { errorMessage } from "../../lib/query.ts";

import { Figure } from "../components/Figure.tsx";
import { useDataset, type DatasetRow } from "../dataset.ts";
import { formatBySemantic } from "./format.ts";

const NUMERIC_SEMANTICS = new Set<ColumnSemantic>([
  "currency",
  "percentage",
  "duration_days",
  "count",
]);

/**
 * `intent: "table"`: la tabla mejorada, con el formato de `columns[].semantic`
 * (moneda, porcentaje, días...) en cada celda.
 *
 * Es deliberadamente propia y no `<DataTable>`: esa tabla solo conoce el
 * formato genérico `"text"|"number"|"boolean"` del dataset (`dataset.ts`), y
 * forzarle la semántica ahí habría significado reescribir su lógica de
 * columnas. Se monta sobre el mismo `DataGrid` y el mismo `Figure` que usa
 * `DataTable` por debajo, así que el marco y la rejilla son los de siempre.
 *
 * Limitación conocida: no reusa el "ver más" por páginas de `DataTable` --
 * aquí se pinta la tabla completa que trajo el dataset.
 */
export function PresentationTable({
  presentation,
}: {
  presentation: Presentation;
}) {
  const state = useDataset(presentation.datasetId);

  const columns = useMemo<GridColumn<DatasetRow>[]>(
    () =>
      presentation.columns.map((column) => ({
        key: column.name,
        label: column.label,
        numeric: NUMERIC_SEMANTICS.has(column.semantic),
        render: (row) =>
          formatBySemantic(row[column.name] ?? null, column.semantic),
        sortValue: (row) => row[column.name] ?? null,
      })),
    [presentation.columns],
  );

  if (state.isPending) {
    return (
      <div className="rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
        Cargando datos…
      </div>
    );
  }

  if (state.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>No pude cargar la tabla</AlertTitle>
        <AlertDescription>{errorMessage(state.error)}</AlertDescription>
      </Alert>
    );
  }

  const dataset = state.data;

  return (
    <Figure
      title={dataset.name}
      aside={
        <span className="text-xs text-muted-foreground tabular-nums">
          {dataset.rowCount.toLocaleString("es")}{" "}
          {dataset.rowCount === 1 ? "fila" : "filas"}
        </span>
      }
    >
      <div className="scroll-quiet max-h-104 overflow-auto">
        <DataGrid columns={columns} rows={dataset.rows} />
      </div>
    </Figure>
  );
}
